import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/* eslint-disable no-return-assign */
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Button, ButtonContainer, Divider, GridColumn, GridItem, GridRow, Icon, Spacer, Typography, useSnackbar } from '@emburse/embark-core';
import { ArrowBackIcon } from '@emburse/embark-icons';
import { $t, useTranslation } from '../../hooks/useTranslation';
import SetupStepper from './components/SetupStepper';
import QuickSetupModal from './components/QuickSetupModal';
import { Start, Departments, Users, ExpenseCategories, CategoryFiltration, CompanyPolicy, GeneralLedgerDimensions, EmployeeDimensions, EmployeeMapping, ExpenseReportDimensions, Review } from './steps';
import { getCurrentStep, getSetupData, getSpreadSheet, uploadSpreadSheet, saveSetupData, saveCompanyPolicy, mapUsersToEmpGLD, setCurrentStep as saveCurrentStep, saveFiltrationOfCategories, getStepStatus } from '../../api/SSI';
import { getCurrenciesSorted } from '../../api/api';
import { CurrencySortBy } from '../../interfaces/Money';
import useLoadingOverlay from '../../hooks/useLoadingOverlay';
import { StepType } from '../../interfaces/SSI';
import { RejectException } from '../../api/common';
import { generateRandomGUID, toBase64, poll, PollingTimeoutException } from '../../util/helpers';
import { logger } from '../../util/logger';
function QuickSetup() {
    useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { applyLoadingState, removeLoadingState } = useLoadingOverlay();
    const stepperRef = useRef(null);
    const viewRef = useRef(null);
    const [currentStep, setCurrentStep] = useState(0);
    const [openModal, setOpenModal] = useState(false);
    const [openErrorsModal, setOpenErrorsModal] = useState(false);
    const [errorsModalData, setErrorsModalData] = useState([]);
    const [stepData, setStepData] = useState();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [currencies, setCurrencies] = useState([]);
    const alertDefault = {
        show: false,
        severity: 'error',
        title: '',
        text: '',
        onClick: () => setOpenErrorsModal(true)
    };
    const alertError = {
        show: true,
        title: $t('We could not upload your data'),
        text: $t('Please fix all errors before trying to upload your data again')
    };
    const alertFEError = {
        show: true,
        title: $t('We could not save your data'),
        text: $t('Some required fields are missing or invalid. Please fix all errors before tying to save your data again')
    };
    const [alert, setAlert] = useState(alertDefault);
    useEffect(() => {
        window.onbeforeunload = () => '';
        window.onunload = () => {
            window.onbeforeunload = null;
        };
    }, []);
    const snackbarHandler = useCallback((message, severity) => {
        enqueueSnackbar(message, { variant: 'alert', AlertProps: { severity } });
    }, [enqueueSnackbar]);
    const catchError = useCallback((err) => {
        let message = err?.Message || err;
        if (String(err).includes('PermissionDeniedException')) {
            message =
                'Unauthorized Access. Please log into an Administrative Account to continue.';
        }
        logger.log(err);
        snackbarHandler(message, 'error');
    }, [snackbarHandler]);
    const stepperSteps = () => [
        'Start',
        'Departments',
        'Users',
        'Expense Categories',
        'Filtration Of Categories',
        'Company Policy',
        'General Ledger Dimensions',
        'Employee Dimensions',
        'Employee Mapping',
        'Expense Report Dimensions',
        'Review'
    ];
    useEffect(() => {
        getCurrenciesSorted(CurrencySortBy.Name).then(setCurrencies);
    }, []);
    useEffect(() => {
        // when coming back to quicksetup from home page banner
        getCurrentStep()
            .then((data) => {
            const step = parseInt(JSON.parse(JSON.stringify(data)), 10);
            if (step > 0) {
                if (stepperRef.current) {
                    // this needs to be - 1 from current step, unless is step 0
                    stepperRef.current.continueForwardNavigation(step - 1, true);
                }
                if (step === stepperSteps().length - 1) {
                    stepperRef.current.setReviewStepComplete();
                }
                setCurrentStep(step);
            }
        })
            .catch(catchError)
            .finally(() => setLoading(false));
    }, [catchError]);
    useEffect(() => {
        if (currentStep > 0) {
            const trimmedStep = stepperSteps()[currentStep].replace(/\s/g, '');
            saveCurrentStep(StepType[trimmedStep]).catch(catchError);
        }
    }, [catchError, currentStep]);
    const loadSetupData = useCallback(() => {
        const trimmedStep = stepperSteps()[currentStep].replace(/\s/g, '');
        setLoading(true);
        getSetupData(StepType[trimmedStep])
            .then((resp) => {
            // over stringified need to parse twice
            let parsedData = JSON.parse(JSON.parse(resp.Data));
            if (Array.isArray(parsedData)) {
                parsedData = parsedData.map((row) => ({ ...row, id: generateRandomGUID() }));
                if (!parsedData.length && stepperSteps()[currentStep] === 'Employee Mapping') {
                    snackbarHandler(`This content is unavailable since you did not fill out the 
                        Employee General Ledger Dimensions in the previous step`, 'warning');
                }
            }
            setStepData(parsedData);
        })
            .catch(catchError)
            .finally(() => setTimeout(() => setLoading(false), 1));
    }, [catchError, currentStep, snackbarHandler]);
    useEffect(() => {
        switch (stepperSteps()[currentStep]) {
            case 'Start':
            case 'General Ledger Dimensions':
            case 'Review':
                return;
            default:
                loadSetupData();
        }
    }, [currentStep, loadSetupData]);
    const getViewRefData = () => (viewRef.current ? viewRef.current.getData() : '');
    const hasEdits = () => {
        const viewData = getViewRefData();
        if (stepperSteps()[currentStep] === 'Employee Dimensions' ||
            stepperSteps()[currentStep] === 'Expense Report Dimensions') {
            const updatedViewData = JSON.parse(viewData).map((item) => {
                const updatedItem = { ...item };
                // eslint-disable-next-line no-param-reassign
                updatedItem.Rows.map((row) => delete row.id);
                return updatedItem;
            });
            return JSON.stringify(updatedViewData) !== JSON.stringify(stepData);
        }
        if (!!viewData && !!stepData) {
            return viewData !== JSON.stringify(stepData);
        }
        return false;
    };
    const downloadSpreadsheet = () => {
        const trimmedStep = stepperSteps()[currentStep].replace(/\s/g, '');
        getSpreadSheet(StepType[trimmedStep], getViewRefData())
            .then((data) => {
            const parsedData = JSON.parse(JSON.stringify(data));
            const fileData = parsedData.FileData;
            const fileName = parsedData.FileName;
            const link = document.createElement('a');
            link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${encodeURIComponent(fileData)}`;
            link.setAttribute('download', fileName);
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        })
            .catch(catchError);
    };
    const moveForward = () => {
        if (stepperRef.current) {
            stepperRef.current.continueForwardNavigation(currentStep, true);
            if (stepperSteps()[currentStep] === 'Expense Report Dimensions') {
                stepperRef.current.setReviewStepComplete();
            }
        }
        viewRef.current = null;
        setStepData('');
        setCurrentStep((prevState) => prevState + 1);
    };
    const pollStepStatus = async (isSpreadsheet = false) => {
        const POLLING_INTERVAL = 2000;
        const POLLING_MAX_ATTEMPTS = 50;
        const POLLING_TIMEOUT_MESSAGE = 'Data upload is taking longer than expected. Please return to the page in a few minutes.';
        const GENERIC_ERROR_LOADING_MESSAGE = 'Internal error occurred. Please re-upload your data and try again.';
        const trimmedStep = stepperSteps()[currentStep].replace(/\s/g, '');
        try {
            if (stepperSteps()[currentStep] === 'Employee Dimensions' ||
                stepperSteps()[currentStep] === 'Expense Report Dimensions') {
                const gldArray = JSON.parse(getViewRefData()).map((item) => item.Label);
                await gldArray.forEach(async (gld, index) => {
                    if (gld) {
                        await poll(() => getStepStatus(StepType[trimmedStep], index + 1), (result) => {
                            const parsedData = JSON.parse(JSON.stringify(result));
                            if (parsedData === 'Error') {
                                return true;
                            }
                            const status = parsedData !== 'Completed';
                            return status;
                        }, POLLING_INTERVAL, POLLING_MAX_ATTEMPTS);
                    }
                });
            }
            else {
                await poll(() => getStepStatus(StepType[trimmedStep]), (result) => {
                    const parsedData = JSON.parse(JSON.stringify(result));
                    if (parsedData === 'Error') {
                        return true;
                    }
                    const status = parsedData !== 'Completed';
                    return status;
                }, POLLING_INTERVAL, POLLING_MAX_ATTEMPTS);
            }
            setErrorsModalData(null);
            setAlert(alertDefault);
            snackbarHandler(`Your data has been successfully ${isSpreadsheet ? 'imported' : 'saved'}`, 'success');
            if (isSpreadsheet) {
                setStepData('');
                loadSetupData();
            }
        }
        catch (err) {
            if (err instanceof RejectException) {
                snackbarHandler(err.message, 'error');
            }
            else if (err instanceof PollingTimeoutException) {
                snackbarHandler(POLLING_TIMEOUT_MESSAGE, 'error');
            }
            else {
                snackbarHandler(GENERIC_ERROR_LOADING_MESSAGE, 'error');
            }
        }
    };
    const saveData = (isSpreadsheet = false, file = '') => {
        setSaving(true);
        applyLoadingState(30000, `Your changes are taking a while to save. Feel free to navigate away from the Setup Wizard,
        and we'll notify you via email when the process is complete.`);
        const trimmedStep = stepperSteps()[currentStep].replace(/\s/g, '');
        const sendData = file || getViewRefData();
        saveSetupData(StepType[trimmedStep], sendData, isSpreadsheet)
            .then(async (data) => {
            const parsedData = JSON.parse(JSON.stringify(data));
            if (parsedData?.ErrorMessages.length) {
                setErrorsModalData(parsedData.ErrorMessages);
                setAlert({ ...alertDefault, ...alertError });
            }
            else {
                await pollStepStatus(isSpreadsheet);
                if (currentStep < stepperSteps().length - 1 && !isSpreadsheet) {
                    moveForward();
                }
            }
        })
            .catch(catchError)
            .then(() => setSaving(false))
            .finally(removeLoadingState);
    };
    const uploadSpreadsheetData = async (e) => {
        const trimmedStep = stepperSteps()[currentStep].replace(/\s/g, '');
        const file = e.target.files[0];
        const fileData = (await toBase64(file));
        uploadSpreadSheet(StepType[trimmedStep], fileData)
            .then((data) => {
            const parsedData = JSON.parse(JSON.stringify(data));
            if (parsedData.ErrorMessages.length) {
                setErrorsModalData(parsedData.ErrorMessages);
                setAlert({ ...alertDefault, ...alertError });
            }
            else {
                setErrorsModalData(null);
                setAlert(alertDefault);
                saveData(true, fileData);
            }
        })
            .catch(catchError);
    };
    const noDataStep = () => {
        let tableData = getViewRefData();
        if (tableData.length) {
            tableData = JSON.parse(tableData);
        }
        const hasLength = tableData.length > 0;
        switch (stepperSteps()[currentStep]) {
            case 'Departments':
                return hasLength ? null : 'department';
            case 'Users':
                return hasLength ? null : 'user';
            case 'Expense Categories':
                return hasLength ? null : 'expense category';
            default:
                return null;
        }
    };
    const validationErrors = () => {
        // eslint-disable-next-line no-prototype-builtins
        if (viewRef.current && viewRef.current.hasOwnProperty('isValid')) {
            return viewRef.current.isValid();
        }
        return [];
    };
    const saveCompanyPolicyData = () => {
        applyLoadingState();
        saveCompanyPolicy(getViewRefData())
            .then(moveForward)
            .catch(catchError)
            .finally(removeLoadingState);
    };
    const saveEmpGLDMappingData = () => {
        applyLoadingState();
        mapUsersToEmpGLD(getViewRefData())
            .then(moveForward)
            .catch(catchError)
            .finally(removeLoadingState);
    };
    const saveCategoryFiltration = () => {
        applyLoadingState();
        saveFiltrationOfCategories(getViewRefData())
            .then(moveForward)
            .catch(catchError)
            .finally(removeLoadingState);
    };
    const nextClickHandler = () => {
        if (!hasEdits()) {
            moveForward();
            return;
        }
        switch (stepperSteps()[currentStep]) {
            case 'General Ledger Dimensions':
                moveForward();
                break;
            case 'Employee Mapping':
                saveEmpGLDMappingData();
                break;
            case 'Filtration Of Categories':
                saveCategoryFiltration();
                break;
            case 'Company Policy':
                saveCompanyPolicyData();
                break;
            default:
                if (noDataStep()) {
                    snackbarHandler(`You need a minimum of 1 ${noDataStep()} to continue`, 'warning');
                    return;
                }
                if (validationErrors().length) {
                    setErrorsModalData(validationErrors());
                    setAlert({ ...alertDefault, ...alertFEError });
                }
                else {
                    setErrorsModalData(null);
                    setAlert(alertDefault);
                    if (currentStep > 0) {
                        saveData();
                    }
                }
        }
    };
    const previousClickHandler = () => {
        viewRef.current = null;
        setErrorsModalData(null);
        setAlert(alertDefault);
        setStepData('');
        setCurrentStep((prevState) => (prevState === 0 ? prevState : prevState - 1));
    };
    const goToReview = () => {
        if (stepperRef.current) {
            stepperRef.current.continueForwardNavigation(currentStep, true);
            stepperRef.current.setReviewStepComplete();
        }
        viewRef.current = null;
        setStepData('');
        setCurrentStep(stepperSteps().length - 1);
    };
    const backToStepClickHandler = (step) => {
        viewRef.current = null;
        setErrorsModalData(null);
        setAlert(alertDefault);
        setStepData('');
        setCurrentStep(step);
    };
    const showNavButtons = () => stepperSteps()[currentStep] !== 'Start' &&
        stepperSteps()[currentStep] !== 'General Ledger Dimensions';
    const errorModalText = () => {
        if (errorsModalData && errorsModalData.length) {
            const isFromServer = errorsModalData
                .map((item) => Object.keys(item).includes('Message'))
                .includes(true);
            if (isFromServer) {
                return errorsModalData.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                _jsxs(Fragment, { children: [!!item?.Line && item?.Line !== -1 && (_jsx(Typography, { children: _jsx("strong", { children: `Line: ${item.Line}` }) })), _jsx(Typography, { children: item.Message }), _jsx(Spacer, { size: 10 })] }, `${index}${item.Message}`)));
            }
            return errorsModalData.map((item) => {
                const mappedCell = item.cell.map((cell) => (_jsx(Typography, { children: `${cell.field}: ${cell.reason}` }, cell.field)));
                return (_jsxs(Fragment, { children: [!!item?.table && (_jsx(Typography, { children: _jsx("strong", { children: `GLD: ${item.table}` }) })), !!item?.row && (_jsx(Typography, { children: _jsx("strong", { children: `Row: ${item.row}` }) })), mappedCell.map((cell) => cell), _jsx(Spacer, { size: 10 })] }, item?.table ? item.table : item.row));
            });
        }
        return _jsx(Typography, { children: null });
    };
    const views = [
        _jsx(Start, { loading: loading, onStart: moveForward }, "Start"),
        _jsx(Departments, { data: stepData, ref: (element) => (viewRef.current = element), upload: uploadSpreadsheetData, download: downloadSpreadsheet, loading: loading, alert: alert }, "Departments"),
        _jsx(Users, { data: stepData, ref: (element) => (viewRef.current = element), upload: uploadSpreadsheetData, download: downloadSpreadsheet, loading: loading, alert: alert, currencies: currencies }, "Users"),
        _jsx(ExpenseCategories, { data: stepData, ref: (element) => (viewRef.current = element), upload: uploadSpreadsheetData, download: downloadSpreadsheet, loading: loading, alert: alert, currencies: currencies }, "ExpenseCategories"),
        _jsx(CategoryFiltration, { data: stepData, ref: (element) => (viewRef.current = element), loading: loading }, "CategoryFiltration"),
        _jsx(CompanyPolicy, { data: stepData, ref: (element) => (viewRef.current = element), currencies: currencies }, "CompanyPolicy"),
        _jsx(GeneralLedgerDimensions, { onContinue: nextClickHandler, onReview: goToReview }, "GeneralLedgerDimensions"),
        _jsx(EmployeeDimensions, { data: stepData, ref: (element) => (viewRef.current = element), loading: loading, upload: uploadSpreadsheetData, download: downloadSpreadsheet, alert: alert }, "EmployeeDimensions"),
        _jsx(EmployeeMapping, { data: stepData, ref: (element) => (viewRef.current = element), loading: loading }, "EmployeeMapping"),
        _jsx(ExpenseReportDimensions, { data: stepData, ref: (element) => (viewRef.current = element), loading: loading, upload: uploadSpreadsheetData, download: downloadSpreadsheet, alert: alert }, "ExpenseReportDimensions"),
        _jsx(Review, { ref: (element) => (viewRef.current = element), onReviewClick: backToStepClickHandler }, "Review")
    ];
    return (_jsxs(GridRow, { children: [_jsx(QuickSetupModal, { open: openErrorsModal, showCancel: false, onConfirm: () => setOpenErrorsModal(false), onClose: () => setOpenErrorsModal(false), header: $t('Import Errors'), confirmButtonText: $t('OK'), body: errorModalText() }), _jsx(Spacer, { size: 40 }), _jsx(GridItem, { sm: 3, style: { maxWidth: '250px' }, children: _jsx(SetupStepper, { currentStep: currentStep, steps: stepperSteps, ref: stepperRef, stepClick: (step) => backToStepClickHandler(step), style: { position: 'fixed' }, saving: saving }) }), _jsxs(GridItem, { sm: 9, children: [_jsx(QuickSetupModal, { open: openModal, onConfirm: () => {
                            window.onbeforeunload = null;
                            window.open('./Home.aspx', '_self');
                        }, onClose: () => setOpenModal(false), header: $t('Heads Up!'), confirmButtonText: $t('Exit'), body: _jsxs(_Fragment, { children: [_jsx(Typography, { children: $t("If you've added data to this page, please click 'Save and Continue' at the bottom of the screen to save it to your system.") }), _jsx(Spacer, { size: 15 }), _jsx(Typography, { children: $t('You can return to complete your Certify Quick Setup anytime you like.') })] }) }), _jsxs(GridColumn, { children: [_jsx(GridItem, { children: views[currentStep] }), showNavButtons() && (_jsxs(GridItem, { children: [_jsx(Spacer, { size: 25 }), _jsx(Divider, { orientation: "horizontal", variant: "fullWidth", style: { width: '100%' }, light: true }), _jsx(Spacer, { size: 15 }), _jsxs(GridRow, { children: [_jsx(GridItem, { style: { minWidth: '50%' }, children: _jsx(Button, { variant: "text", startIcon: _jsx(Icon, { iconUrl: ArrowBackIcon }), onClick: previousClickHandler, size: "small", disabled: loading || saving, children: $t('Previous') }) }), currentStep !== stepperSteps().length - 1 && (_jsx(GridItem, { style: { minWidth: '50%' }, children: _jsxs(ButtonContainer, { alignButtons: "right", children: [_jsx(Button, { variant: "outlined", onClick: () => setOpenModal(true), size: "small", children: $t('Exit') }), _jsx(Button, { onClick: nextClickHandler, size: "small", disabled: loading || saving, children: $t('Save & Continue') })] }) }))] })] }))] })] })] }));
}
export default QuickSetup;
